// The customer menu layout: which section tab (Coffee & Beverages, Food, …)
// each category sits under and in what order, plus the home page's round
// quick-link rows. An admin arranges it in Products → Categories → Menu layout
// (GET/PUT /api/menu/layout, read through hooks/useMenuLayout.js); the
// constants below are the built-in defaults used until a layout is saved.
// One layout serves every store — each store only shows the categories it sells.

export const MENU_SECTIONS = [
  {
    name: 'Coffee & Beverages',
    categories: ['Cold Classics', 'Hot Classics', 'Mocktails & Smoothies', 'Matcha Iced Latte', 'Others', 'Iced Teas', 'Smoothies & Coolers', 'Fresh Juices'],
  },
  {
    name: 'Food',
    categories: ['Breakfast All Day', 'Small Bites', 'Sandwiches & Wraps', 'Meal Bowls', 'Healthy Wholewheat Pasta', 'Homestyle Healthy Tiffin', 'Quick Bites', 'Croissants'],
  },
  {
    name: 'Healthy Desserts',
    categories: ['Healthy Desserts'],
  },
  {
    name: 'Waffles',
    categories: ['Penguin Waffles'],
  },
  {
    name: 'Marol Offer',
    categories: ['Marol Offer'],
  },
  {
    name: 'Merchandise',
    categories: ['Merchandise'],
  },
];

// The home page's quick-link rows (owner ask 2026-10-24): The Coffee Bar, The
// Kitchen (the partner kitchen's pasta, sandwiches, bowls, breakfast) and The
// Bakehouse & Bites (waffles, croissants, quick bites). A row only shows at a
// store that sells one of its categories, so Airoli gets all three while Marol
// and Nahar — no kitchen — get The Coffee Bar and The Bakehouse & Bites.
export const DEFAULT_HOME_ROWS = [
  {
    title: 'The Coffee Bar',
    links: [
      { category: 'Cold Classics', label: 'Cold Coffee' },
      { category: 'Hot Classics', label: 'Hot Coffee' },
      { category: 'Matcha Iced Latte', label: 'Matcha' },
      { category: 'Mocktails & Smoothies', label: 'Coolers' },
      { category: 'Iced Teas', label: 'Iced Tea' },
      { category: 'Smoothies & Coolers', label: 'Smoothies' },
      { category: 'Fresh Juices', label: 'Juices' },
    ],
  },
  {
    title: 'The Kitchen',
    links: [
      { category: 'Healthy Wholewheat Pasta', label: 'Pasta' },
      { category: 'Sandwiches & Wraps', label: 'Sandwiches' },
      { category: 'Meal Bowls', label: 'Bowls' },
      { category: 'Breakfast All Day', label: 'Breakfast' },
      { category: 'Small Bites', label: 'Small Bites' },
      { category: 'Homestyle Healthy Tiffin', label: 'Tiffin' },
    ],
  },
  {
    title: 'The Bakehouse & Bites',
    links: [
      { category: 'Penguin Waffles', label: 'Waffles' },
      { category: 'Croissants', label: 'Croissants' },
      { category: 'Quick Bites', label: 'Quick Bites' },
      { category: 'Healthy Desserts', label: 'Desserts' },
    ],
  },
];

// Emoji shown in a quick-link circle until the category has an uploaded image.
const QUICK_LINK_ICONS = {
  'Cold Classics': '❄', 'Hot Classics': '☕', 'Matcha Iced Latte': '🍵', 'Mocktails & Smoothies': '🥤',
  'Iced Teas': '🧊', 'Smoothies & Coolers': '🍹', 'Fresh Juices': '🧃', 'Breakfast All Day': '🍳',
  'Small Bites': '🥙', 'Sandwiches & Wraps': '🥪', 'Meal Bowls': '🥗', 'Healthy Wholewheat Pasta': '🍝',
  'Homestyle Healthy Tiffin': '🍱', 'Penguin Waffles': '🧇', 'Croissants': '🥐', 'Quick Bites': '🍿',
  'Healthy Desserts': '🍮',
};

export const MORE_SECTION_NAME = 'More';

// App-only categories (Business Rule #62) — e.g. "Signature Drinks": admin-made
// categories that live in the app only. A member keeps its own Rista category;
// GET /menu stamps it `also_in: {<app-only name>: <position>}` so it lists in
// both places. Ordering, cart and pricing only ever read `item.category`.

/**
 * Every category an item lists under: its own category first, then any
 * app-only category it was stamped into.
 * @param {{category?: string, also_in?: Object<string, number>}} item
 * @returns {string[]}
 */
export function categoriesOfItem(item) {
  const own = item?.category || 'Others';
  return [own, ...Object.keys(item?.also_in || {}).filter(c => c && c !== own)];
}

/**
 * The distinct categories a menu payload lists under, own categories and
 * app-only ones, in first-seen order.
 * @param {Object[]} items
 * @returns {string[]}
 */
export function menuCategoryNames(items = []) {
  return [...new Set(items.flatMap(categoriesOfItem))];
}

/**
 * Whether `category` is an app-only category in this payload (no item has it
 * as its own category, at least one is stamped into it).
 * @param {Object[]} items
 * @param {string} category
 * @returns {boolean}
 */
export function isAppOnlyCategory(items = [], category) {
  if (!category) return false;
  let stamped = false;
  for (const i of items) {
    if ((i.category || 'Others') === category) return false;
    if (i.also_in && i.also_in[category] !== undefined) stamped = true;
  }
  return stamped;
}

/**
 * The items to list under `category`: those whose own category it is, then
 * the app-only members in the admin's order.
 * @param {Object[]} items
 * @param {string} category
 * @returns {Object[]}
 */
export function itemsInCategory(items = [], category) {
  const own = [];
  const extra = [];
  items.forEach(i => {
    if ((i.category || 'Others') === category) own.push(i);
    else if (i.also_in && i.also_in[category] !== undefined) extra.push(i);
  });
  extra.sort((a, b) => a.also_in[category] - b.also_in[category]);
  return [...own, ...extra];
}

/**
 * The sections to offer for a branch's categories: the layout's sections, plus
 * a trailing "More" section holding every category no section lists (in the
 * order given), so a new Rista category always gets a tab.
 * @param {string[]} categories - the branch's category names, in display order
 * @param {{name: string, categories: string[]}[]} [sections] - the layout (defaults to MENU_SECTIONS)
 * @returns {{name: string, categories: string[]}[]}
 */
export function sectionsFor(categories = [], sections = MENU_SECTIONS) {
  const listed = new Set(sections.flatMap(s => s.categories));
  const unlisted = categories.filter(c => c && !listed.has(c));
  if (unlisted.length === 0) return sections;
  return [...sections, { name: MORE_SECTION_NAME, categories: unlisted }];
}

/**
 * A branch's categories in layout order — section by section, category by
 * category — with any category no section lists appended in the order given.
 * Drives the home page's Categories grid.
 * @param {string[]} categories - the branch's categories
 * @param {{name: string, categories: string[]}[]} [sections] - the layout (defaults to MENU_SECTIONS)
 * @returns {string[]}
 */
export function orderCategories(categories = [], sections = MENU_SECTIONS) {
  const have = new Set(categories);
  const ordered = sections.flatMap(s => s.categories).filter(c => have.has(c));
  const seen = new Set(ordered);
  categories.forEach(c => { if (c && !seen.has(c)) { ordered.push(c); seen.add(c); } });
  return ordered;
}

/**
 * The home quick-link rows a branch can show: each row's links kept only when
 * the branch sells that category, each link given its fallback emoji, and
 * rows left empty dropped.
 * @param {{title: string, links: {category: string, label: string}[]}[]} rows
 * @param {string[]} branchCategories
 * @returns {{title: string, links: {category: string, label: string, icon: string}[]}[]}
 */
export function homeRowsFor(rows = DEFAULT_HOME_ROWS, branchCategories = []) {
  const have = new Set(branchCategories);
  return rows
    .map(r => ({
      title: r.title,
      links: (r.links || [])
        .filter(l => have.has(l.category))
        .map(l => ({ category: l.category, label: l.label || l.category, icon: QUICK_LINK_ICONS[l.category] || '🍽️' })),
    }))
    .filter(r => r.links.length > 0);
}
